import type { getPOI$result } from '$houdini';
import type { AccommodationMichelinPoiData, AccommodationPoiData, RestaurantMichelinPoiData, RestaurantPoiData, TourismPoiData } from '$lib/components/pois/types';
import type { AggregateRating, GeoCoordinates, PostalAddress, Thing, WithContext } from 'schema-dts';
import { isAccommodationMichelinPoi, isAnyAccommodationPoi, isRestaurantMichelinPoi, isRestaurantPoi, isTourismPoi } from '$lib/components/pois/utils';
import { POIS_HRT_TYPENAMES_LIST } from '$lib/constants/poilist';

export type HRTPoiDataType = AccommodationPoiData | RestaurantPoiData | TourismPoiData | RestaurantMichelinPoiData | AccommodationMichelinPoiData;
type DefaultSchemaType = Thing | WithContext<Thing> ;
type PoiResult = getPOI$result['getPOIFromId']['poi'];
type Address = PoiResult['address'];
interface Coordinates { lat: number; lng: number }
type SchemaType = 'Hotel' | 'Restaurant' | 'TouristAttraction' | 'Thing';
type Typename = PoiResult['__typename'];

function mapPoiTypenameToSchemaSubType(typename: Typename): SchemaType {
	switch (typename) {
		case 'AccommodationPOI':
		case 'AccommodationMichelinPOI':
			return 'Hotel';
		case 'RestaurantPOI':
		case 'RestaurantMichelinPOI':
			return 'Restaurant';
		case 'TourismPOI':
			return 'TouristAttraction';
		default:
			return 'Thing';
	}
}

function buildAddressSchema(address: Address): PostalAddress {
	return {
		'@type': 'PostalAddress',
		'streetAddress': address.formattedAddress ?? undefined,
		'addressLocality': address.formattedCity ?? undefined,
		'postalCode': address.zipCode ?? undefined,
		// Recommended to be ISO 3166-1 alpha-2 format (2 letters, for example "US").
		// For backward compatibility, a 3-letter ISO 3166-1 alpha-3 can also be used or full country name
		'addressCountry': address?.country?.code ?? undefined,
	};
}

function buildGeoSchema(coordinates: Coordinates): GeoCoordinates {
	return {
		'@type': 'GeoCoordinates',
		'latitude': coordinates.lat,
		'longitude': coordinates.lng,
	};
}

function buildAggregateRatingSchema(score: number | null, count: number | null, bestRating = 10, worstRating = 0): AggregateRating | undefined {
	if (score === null || count === null) {
		return;
	}

	return {
		'@type': 'AggregateRating',
		'ratingValue': Number(score),
		'reviewCount': Number(count),
		'bestRating': bestRating,
		'worstRating': worstRating,
	};
}

export function getPriceRangeForSchemaPriceRangeField(length: number) {
	return Array.from({ length }, (_, __) => '$').join('');
}

function getPriceRangeForSchemaAwardField(description?: string | null, millesime?: number | null) {
	if (!description) {
		return;
	}
	return `MICHELIN Guide: ${description} ${millesime ?? ''}`;
}

function getImageFromDatasheetForSchemaImageField(images?: HRTPoiDataType['images']): string | undefined {
	if (!images?.length) {
		return;
	}

	const firstImageElement = images[0];
	const imageKeys = Object.keys(firstImageElement).filter(key => /^w\d+$/.test(key));
	const lastImageKey = imageKeys.at(-1) as Exclude<keyof typeof firstImageElement, 'copyright'>;

	return lastImageKey ? firstImageElement[lastImageKey] : undefined;
}

export function buildDatasheetSchema(poi?: PoiResult): DefaultSchemaType | undefined {
	if (!poi || !POIS_HRT_TYPENAMES_LIST.includes(poi.__typename)) {
		return;
	}

	const {
		__typename,
		description,
		coordinates,
		address,
		name,
		images,
		phone,
	} = poi as HRTPoiDataType;

	const isMichelinGuidePoi = isAccommodationMichelinPoi(poi) || isRestaurantMichelinPoi(poi);

	return {
		'@context': 'https://schema.org',
		'@type': mapPoiTypenameToSchemaSubType(__typename),
		'name': name,
		'description': description ?? undefined,
		'image': getImageFromDatasheetForSchemaImageField(images),
		'telephone': phone ?? undefined,
		'address': buildAddressSchema(address),
		'geo': buildGeoSchema(coordinates),
		'priceRange': isMichelinGuidePoi && poi?.priceCategory?.count
			? getPriceRangeForSchemaPriceRangeField(poi.priceCategory.count)
			: undefined,
		'servesCuisine': (isRestaurantMichelinPoi(poi) && poi?.meals?.cuisineTypes) ? poi?.meals?.cuisineTypes?.join(', ') : undefined,
		'paymentAccepted': (isRestaurantMichelinPoi(poi) && poi?.paymentModes) ? poi?.paymentModes?.join(', ') : undefined,
		'award': (isMichelinGuidePoi || isTourismPoi(poi))
			? getPriceRangeForSchemaAwardField(poi?.distinctionDescription, poi?.distinctionMillesime)
			: undefined,
		'aggregateRating': ((isAnyAccommodationPoi(poi) || isRestaurantPoi(poi)) && poi?.review)
			? buildAggregateRatingSchema(poi.review?.score, poi.review?.count)
			: undefined,
	} satisfies DefaultSchemaType;
}
